import { queryClient } from '@/router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { UserRole } from '@/lib/queries/business/types'
import { inviteMemberMutation } from '@/lib/queries/business/v1/user/invite/mutations'

import { type InviteMemberDialogProps } from './invite-member-dialog'

export type UseInviteMemberDialogParams = Pick<InviteMemberDialogProps, 'onClose'>

export const formSchema = z.object({
  email: z.string().email(),
})

export const useInviteMemberDialog = ({ onClose }: UseInviteMemberDialogParams) => {
  const [adminEmails, setAdminEmails] = useState<string[]>([])
  const [userEmails, setUserEmails] = useState<string[]>([])

  const adminEmailForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  })
  const userEmailForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  })

  const { mutateAsync: inviteMember, isPending: isPendingMemberInviting } = useMutation(inviteMemberMutation)

  const isDisabledInviteButton = adminEmails.length === 0 && userEmails.length === 0

  function handleSubmitAdminInput(data: z.infer<typeof formSchema>) {
    if (!adminEmails.includes(data.email)) {
      setAdminEmails([...adminEmails, data.email])
    }
    adminEmailForm.reset({ email: '' })
  }

  function handleSubmitUserInput(data: z.infer<typeof formSchema>) {
    if (!userEmails.includes(data.email)) {
      setUserEmails([...userEmails, data.email])
    }
    userEmailForm.reset({ email: '' })
  }

  function handleClickAdminEmailChip(value: string) {
    setAdminEmails((adminEmails) => adminEmails.filter((email) => email !== value))
  }

  function handleClickUserEmailChip(value: string) {
    setUserEmails((userEmails) => userEmails.filter((email) => email !== value))
  }

  async function handleClickInviteButton() {
    await inviteMember(
      {
        users: [
          ...adminEmails.map((email) => ({
            email,
            role: UserRole.ADMIN,
          })),
          ...userEmails.map((email) => ({
            email,
            role: UserRole.USER,
          })),
        ],
      },
      {
        onSuccess: () => {
          setTimeout(async () => {
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v1.user.members.pending.index],
            })
          }, 1000)
          toast.success('Invited members successfully!')
          onClose()
        },
        onError: () => {
          toast.error('Invite failed. Please try again later.')
        },
      }
    )
  }

  function reset() {
    setAdminEmails([])
    setUserEmails([])
  }

  /**
   * reset form input values when dialog closed.
   */
  useEffect(() => {
    return () => {
      reset()
    }
  }, [])

  return {
    adminEmailForm,
    userEmailForm,
    adminEmails,
    userEmails,
    isDisabledInviteButton,
    isPendingMemberInviting,
    handleSubmitAdminInput,
    handleSubmitUserInput,
    handleClickAdminEmailChip,
    handleClickUserEmailChip,
    handleClickInviteButton,
  }
}
